import { useEffect, useRef } from 'react';

import { format } from 'date-fns';
import isNil from 'lodash/isNil';

import { DATE_FORMAT } from '@app/consts';
import { useNocSmPageVisitEvent } from '@app/hooks/sales-manago';
import { accommodationSlice, checkoutDetailsSlice, useAppSelector } from '@app/state';

import { IPageVisitPayload, SmEventType } from '@noc-sm-plugin';

export const useNocSmCheckoutPageVisit = () => {
    const filters = useAppSelector(accommodationSlice.selectors.selectFilters);
    const checkoutDetails = useAppSelector(checkoutDetailsSlice.selectors.selectInputData);
    const checkoutSummary = useAppSelector(checkoutDetailsSlice.selectors.selectCheckoutSummary);
    const accommodation = useAppSelector(accommodationSlice.selectors.selectCurrentAccommodation);
    const eventPayload = useRef<IPageVisitPayload | undefined>();
    const completed = useRef<boolean>(false);
    const { pushPageVisitEvent } = useNocSmPageVisitEvent();

    useEffect(() => {
        if(isNil(checkoutDetails) || isNil(checkoutSummary) || isNil(accommodation) || isNil(filters)) {
            return;
        }
        const { guestsCount, childrenCount, checkIn, checkOut } = filters;
        const { type, internalAmenityGroups } = accommodation;


        eventPayload.current = {
            products: checkoutDetails?.accommodationId?.replace(/^\D+/g, ''),
            value: checkoutSummary?.guestPaymentAmount,
            /**
             * Based on business requirements array order should be:
             *
             * 1) detail[1] - event name (“checkout”)
             * 2) detail[2] - accom. city
             * 3) detail[3] - accom. region (e.g. seaside, Mazury, Tatry etc.)
             * 4) detail[4] - accom. category (e.g. apartment)
             * 5) detail[5] - stay dates (“DD-MONTH-YYYY - DD-MONTH-YYYY” format)
             * 6) detail[6] - guests (“X_adults, Y_children” format)
             * 7) detail[7] - top amenities (e.g. parking, pool… - same as listed in offerFacilities in Offer GA event)
             */
            details:[
                'checkout',
                accommodation?.settlement?.name ?? '',
                '', // TODO: region will bee added when we switch to NocAPI instead of Accommodation GQL (it is should for now)
                `${type?.name ?? ''}`,
                `${format(new Date(checkIn ?? Date.now()),DATE_FORMAT.salesManagoEventDateFormat)} - ${format(new Date(checkOut || Date.now()),DATE_FORMAT.salesManagoEventDateFormat)}`,
                `${guestsCount}_adults, ${childrenCount}_children`,
                (internalAmenityGroups ?? []).map(group=>group?.label).filter(Boolean).join(', ')
            ]
        };
    },[checkoutDetails, checkoutSummary, accommodation, filters]);

    /**
     * Send event to NocSmPlugin on component unmount
     */
    useEffect(() => {
        const handler = ()=>{
            if (!isNil(eventPayload.current) && !completed.current) {
                completed.current = true;
                pushPageVisitEvent(eventPayload.current, SmEventType.CART);
            }
        };

        window.addEventListener('beforeunload', handler)

        return () => {
            window.removeEventListener('beforeunload', handler);
            handler();
        }
    }, []);
}